<template>
    <div class="customer-service-agent">

        <!-- Chat Messages Container -->
        <div class="chat-messages" ref="chatMessages">
            <!-- Rate Limit Indicator (at top if showing metadata) -->
            <rate-limit-indicator
                v-if="showMetadata"
                :remaining="rateLimitRemaining"
                :show="showMetadata"
                class="mb-3"
            />

            <!-- Clear History Button (at top, only show if there are responses) -->
            <div v-if="responses.length > 0" class="clear-history-container top-position">
                <button
                    type="button"
                    class="clear-history-btn"
                    @click="clearHistory"
                    title="Clear chat history"
                >
                    <i class="fas fa-trash-alt"></i>
                    <span>Clear History</span>
                </button>
            </div>

            <!-- Welcome Message -->
            <div v-if="responses.length === 0 && !isLoading" class="welcome-message">
                <div class="welcome-content">
                    <div class="welcome-logo">
                        <!-- Custom logo or fallback to icon -->
                        <img 
                            v-if="logoUrl" 
                            :src="logoUrl" 
                            :alt="logoAlt"
                            class="logo-image"
                            @error="handleLogoError"
                        />
                        <div v-else class="logo-placeholder" :style="{ background: logoGradient }">
                            <i class="fas fa-building"></i>
                        </div>
                    </div>
                    <div class="welcome-text">
                        <p class="mb-0">{{ welcomeMessage }}</p>
                    </div>
                </div>
            </div>

            <!-- Chat Conversation -->
            <div v-for="response in responses" :key="response.id" class="conversation-pair">
                <!-- User Question -->
                <div class="user-message">
                    <div class="message-content">
                        <div class="message-bubble user-bubble">
                            {{ response.question }}
                        </div>
                    </div>
                </div>

                <!-- Bot Response or Loading -->
                <div class="bot-message">
                    <div class="message-content full-width">
                        <!-- Show loading if response is pending -->
                        <div v-if="response.isPending" class="typing-indicator-simple">
                            <div class="typing-dots">
                                <span class="dot"></span>
                                <span class="dot"></span>
                                <span class="dot"></span>
                            </div>
                        </div>
                        <!-- Show actual response when available -->
                        <response-display
                            v-else
                            :response="response"
                            :show-metadata="showMetadata"
                            :is-latest="isLatestResponse(response)"
                            :typewriter-effect="shouldShowTypewriter(response)"
                            :full-width="true"
                            @copy="copyToClipboard"
                            class="chat-response full-width-response"
                        />

                        <!-- Structured input field (EVT-2460) — rendered under the latest completed
                             response when the agent has requested a specific answer shape instead of
                             free text. Only the email field types are implemented; the remaining
                             types (text, checkbox, radio, dropdown, yes_no) each get their own case
                             here in a future ticket, and safely render nothing until then.
                             'handoff_email' is the handoff's own capture and 'email' a plain
                             request_structured_input ask — identical inputs, but only the former
                             completes an escalation, so the two must stay distinguishable. -->
                        <div v-if="showStructuredInput(response)" class="structured-input">
                            <template v-if="isEmailFieldType(response.expectedInput.field_type)">
                                <label class="structured-input__label">{{ response.expectedInput.label }}</label>
                                <div class="structured-input__row">
                                    <input
                                        type="email"
                                        v-model="structuredInputValue"
                                        class="structured-input__field"
                                        placeholder="you@example.com"
                                        :disabled="isLoading"
                                        @keyup.enter="submitStructuredInput(response)"
                                    />
                                    <button
                                        type="button"
                                        class="structured-input__submit"
                                        :disabled="isLoading"
                                        @click="submitStructuredInput(response)"
                                    >Submit</button>
                                </div>
                                <div v-if="structuredInputError" class="structured-input__error">{{ structuredInputError }}</div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Fixed Input Area at Bottom -->
        <div class="chat-input-container">
            <!-- Error Display above input -->
            <error-message
                v-if="errorMessage"
                :error="errorMessage"
                :error-type="errorType"
                @retry="retryLastQuestion"
                @clear="clearError"
                class="mb-2"
            />

            <!-- EVT-2560: once a handoff is recorded the conversation belongs to a human, so the
                 composer is replaced rather than merely disabled — leaving a live box would invite
                 replies that either never reach the person handling the ticket, or escalate the
                 same problem a second time. -->
            <div v-if="sessionEnded" class="session-ended">
                <p class="session-ended__note">This conversation has been passed to our team.</p>
                <button type="button" class="session-ended__restart" @click="startNewChat">
                    Start a new chat
                </button>
            </div>

            <!-- Question Input Form -->
            <question-input
                v-else
                v-model="currentQuestion"
                :is-loading="isLoading"
                :character-limit="characterLimit"
                :error="inputError"
                @submit="submitQuestion"
                @input="clearInputError"
                placeholder="Type your question here..."
            />
        </div>
    </div>
</template>

<script>
import QuestionInput from './components/QuestionInput.vue'
import ResponseDisplay from './components/ResponseDisplay.vue'
import ErrorMessage from './components/ErrorMessage.vue'
import RateLimitIndicator from './components/RateLimitIndicator.vue'
import aiFaqApi from '../mixins/aiFaqApi.js'

export default {
    name: 'CustomerServiceAgent',

    components: {
        QuestionInput,
        ResponseDisplay,
        ErrorMessage,
        RateLimitIndicator
    },

    mixins: [aiFaqApi],

    props: {
        occasionId: {
            type: [Number, String],
            required: true
        },
        apiEndpoint: {
            type: String,
            default: '/api/v0/ai/ask-question'
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        maxQuestions: {
            type: Number,
            default: 5
        },
        characterLimit: {
            type: Number,
            default: 500
        },
        participantId: {
            type: [Number, String],
            default: null
        },
        historyExpiryHours: {
            type: Number,
            default: 24 // 24 hours by default
        },
        welcomeMessage: {
            type: String,
            default: "Hello! I'm your customer service assistant. How can I help you today?"
        },
        logoUrl: {
            type: String,
            default: null // Will use default icon if not provided
        },
        logoAlt: {
            type: String,
            default: 'Organization Logo'
        },
        organizationName: {
            type: String,
            default: 'Organization'
        },
        brandColor: {
            type: String,
            default: '#007bff' // Default blue
        }
    },

    data() {
        return {
            currentQuestion: '',
            responses: [],
            isLoading: false,
            errorMessage: '',
            errorType: null,
            inputError: '',
            rateLimitRemaining: 10,
            lastQuestion: '',
            typedResponseIds: new Set(), // Track which responses have been typed
            // EVT-2560: set once a handoff is recorded. The conversation is over at that point —
            // a human has it — so further messages in the same session would either be lost to
            // whoever picks up the ticket or trigger a second escalation for the same problem.
            sessionEnded: false,
            structuredInputValue: '',
            structuredInputError: ''
        }
    },

    computed: {
        reversedResponses() {
            return [...this.responses].reverse()
        },
        
        logoGradient() {
            // Create a gradient from brandColor to a slightly darker version
            const darkColor = this.darkenColor(this.brandColor, 20)
            return `linear-gradient(135deg, ${this.brandColor}, ${darkColor})`
        }
    },

    mounted() {
        // Load chat history when component mounts
        this.loadChatHistory()
    },

    beforeDestroy() {
        // Save chat history when component is about to be destroyed
        this.saveChatHistory()
    },

    watch: {
        // Save chat history whenever responses change
        responses: {
            handler() {
                this.saveChatHistory()
            },
            deep: true
        },

        // Save when rate limit changes
        rateLimitRemaining() {
            this.saveChatHistory()
        }
    },

    methods: {
        async submitQuestion() {
            // Validate input
            if (!this.validateQuestion()) {
                return
            }
            this.clearInputError()
            const question = this.currentQuestion.trim()
            this.currentQuestion = ''

            await this.sendMessage(question)
        },

        // EVT-2460: submits the agent-requested structured field through the exact same request
        // path as a typed message — the backend has no separate endpoint for it, the value just
        // becomes the next chat message. The field type is echoed back untouched so the backend can
        // tell a handoff email capture from a plain structured email ask.
        async submitStructuredInput(response) {
            const value = (this.structuredInputValue || '').trim()
            const fieldType = response.expectedInput && response.expectedInput.field_type

            if (this.isEmailFieldType(fieldType) && !this.isValidEmailFormat(value)) {
                this.structuredInputError = 'Please enter a valid email address'
                return
            }

            this.structuredInputError = ''
            this.structuredInputValue = ''

            await this.sendMessage(value, fieldType)
        },

        isValidEmailFormat(value) {
            // Deliberately simple — this only needs to catch obviously-malformed input before it
            // leaves the browser. The backend/model remains the source of truth for anything more
            // nuanced, exactly as it is for a typed reply today.
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        },

        // Shared submission path for both a typed question and a submitted structured field —
        // identical from here on, since the backend only ever sees the next chat message either way.
        // expectedFieldType is only set on the structured-field path (EVT-2460) and triggers the
        // matching server-side format check; a typed question always omits it.
        async sendMessage(text, expectedFieldType = null) {
            this.clearError()
            this.structuredInputError = ''
            // Any message going out — typed or a submitted structured field — means the
            // participant has moved on from whatever was previously sitting in the structured
            // field. Without this, an abandoned invalid value (rejected, then never resubmitted
            // because the participant sent a normal message instead) would resurface pre-filled
            // the next time a structured field of the same type is requested later in the
            // conversation.
            this.structuredInputValue = ''
            this.lastQuestion = text

            // Create a pending response immediately to show the question
            const pendingResponse = {
                id: Date.now(),
                question: text,
                isPending: true,
                timestamp: new Date()
            }

            // Add to responses
            this.responses.push(pendingResponse)

            // Scroll to bottom to show the new question
            this.$nextTick(() => {
                this.scrollToBottom()
            })

            // Emit event
            this.$emit('question-submitted', {
                question: text,
                occasionId: this.occasionId,
                participantId: this.participantId,
            })

            const conversationHistory = this.buildConversationHistory()

            try {
                const response = await this.askQuestion(text, this.occasionId, this.participantId, conversationHistory, expectedFieldType)
                this.handleSuccessResponse(response, pendingResponse)
            } catch (error) {
                this.handleErrorResponse(error, pendingResponse)
            }
        },

        buildConversationHistory() {
            return this.responses
                .filter(r => !r.isPending && r.answer)
                .slice(-6)
                .map(r => ({ question: r.question, answer: r.answer }))
        },

        validateQuestion() {
            const question = this.currentQuestion.trim()

            if (!question) {
                this.inputError = 'Please enter a question'
                return false
            }

            if (question.length > this.characterLimit) {
                this.inputError = `Question must be no more than ${this.characterLimit} characters long`
                return false
            }

            // Basic HTML/script detection
            if (/<[^>]*>/.test(question)) {
                this.inputError = 'HTML tags are not allowed'
                return false
            }

            return true
        },

        handleSuccessResponse(apiResponse, pendingResponse) {
            // Find the pending response and update it
            const responseIndex = this.responses.findIndex(r => r.id === pendingResponse.id)
            if (responseIndex !== -1) {
                // Update the existing pending response with the API response
                const updatedResponse = {
                    ...pendingResponse,
                    answer: apiResponse.answer,
                    sources: apiResponse.sources || [],
                    cached: apiResponse.cached || false,
                    tokensUsed: apiResponse.tokens_used,
                    model: apiResponse.model,
                    rateLimitRemaining: apiResponse.rate_limit_remaining,
                    expectedInput: apiResponse.expected_input || null,
                    isPending: false // Remove pending state
                }

                // Replace the pending response with the complete response
                this.$set(this.responses, responseIndex, updatedResponse)

                // Mark this response as one that should show typewriter effect
                this.typedResponseIds.add(updatedResponse.id)

                // EVT-2560: a recorded handoff closes the conversation.
                if (apiResponse.session_ended) {
                    this.sessionEnded = true
                }

                // Update rate limit
                if (typeof apiResponse.rate_limit_remaining === 'number') {
                    this.rateLimitRemaining = apiResponse.rate_limit_remaining
                }

                // Limit history if needed
                if (this.responses.length > this.maxQuestions) {
                    this.responses = this.responses.slice(-this.maxQuestions)
                }

                // Scroll to bottom of chat
                this.$nextTick(() => {
                    this.scrollToBottom()
                })

                // Also scroll during typing animation
                setTimeout(() => {
                    this.scrollToBottom()
                }, 100)

                // Emit event
                this.$emit('response-received', updatedResponse)
            }
        },

        handleErrorResponse(error, pendingResponse) {
            console.error('AI FAQ Error:', error)

            // Remove the pending response since there was an error
            if (pendingResponse) {
                const responseIndex = this.responses.findIndex(r => r.id === pendingResponse.id)
                if (responseIndex !== -1) {
                    this.responses.splice(responseIndex, 1)
                }
            }

            let errorMessage = 'An unexpected error occurred. Please try again.'
            let errorType = 'general'

            if (error.response) {
                const status = error.response.status
                const data = error.response.data

                if (status === 429) {
                    errorType = 'rate-limit'
                    errorMessage = 'Too many requests. Please wait a moment before asking another question.'
                    this.rateLimitRemaining = 0
                    this.$emit('rate-limit-exceeded', { error: data })
                } else if (status === 400 && data.error) {
                    errorType = 'validation'
                    errorMessage = data.error
                } else if (status === 404) {
                    errorType = 'not-found'
                    errorMessage = 'Event not found. Please check the event details.'
                } else if (status >= 500) {
                    errorType = 'server'
                    errorMessage = 'Server error. Our team has been notified. Please try again later.'
                }
            } else if (error.code === 'NETWORK_ERROR') {
                errorType = 'network'
                errorMessage = 'Network error. Please check your connection and try again.'
            }

            this.errorMessage = errorMessage
            this.errorType = errorType

            this.$emit('error-occurred', {
                error: error,
                message: errorMessage,
                type: errorType
            })
        },

        retryLastQuestion() {
            if (this.lastQuestion) {
                this.currentQuestion = this.lastQuestion
                this.clearError()
                this.submitQuestion()
            }
        },

        clearError() {
            this.errorMessage = ''
            this.errorType = null
        },

        clearInputError() {
            this.inputError = ''
        },

        clearHistory() {
            this.clearChatHistory() // This will clear both memory and localStorage
            this.$emit('history-cleared')
        },

        // EVT-2560: starts a genuinely separate conversation. The session identifier is not reset
        // here — clearChatHistory() calls EVT-2566's resetChatSession(), which is the one place
        // that decides what a session is. Two mechanisms for that is the split EVT-2566 closed.
        startNewChat() {
            this.clearChatHistory()
            this.$emit('history-cleared')
        },

        copyToClipboard(text) {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$emit('text-copied', text)
                }).catch(err => {
                    console.error('Clipboard API failed:', err)
                    this.fallbackCopy(text)
                })
            } else {
                this.fallbackCopy(text)
            }
        },

        fallbackCopy(text) {
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            textArea.style.top = '-999999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()

            try {
                const successful = document.execCommand('copy')
                if (successful) {
                    this.$emit('text-copied', text)
                } else {
                    console.error('Fallback copy failed')
                }
            } catch (err) {
                console.error('Fallback copy error:', err)
            } finally {
                document.body.removeChild(textArea)
            }
        },

        scrollToBottom() {
            const chatMessages = this.$refs.chatMessages
            if (chatMessages) {
                chatMessages.scrollTop = chatMessages.scrollHeight
                // Force smooth scrolling behavior
                chatMessages.scrollTo({
                    top: chatMessages.scrollHeight,
                    behavior: 'smooth'
                })
            }
        },

        isLatestResponse(response) {
            return this.responses.length > 0 && this.responses[this.responses.length - 1].id === response.id
        },

        // Both field types render the same email input. 'handoff_email' is set by the backend for
        // the handoff's own capture; 'email' is a plain request_structured_input ask.
        isEmailFieldType(fieldType) {
            return fieldType === 'email' || fieldType === 'handoff_email'
        },

        // EVT-2460: only the latest, completed response can have an active structured field — once
        // the conversation moves on, an earlier turn's request is no longer actionable.
        showStructuredInput(response) {
            return !response.isPending
                && this.isLatestResponse(response)
                && !!(response.expectedInput && this.isEmailFieldType(response.expectedInput.field_type))
        },

        shouldShowTypewriter(response) {
            // Only show typewriter effect if:
            // 1. It's the latest response AND
            // 2. It hasn't been typed before (not loaded from localStorage)
            return this.isLatestResponse(response) && !this.typedResponseIds.has(response.id)
        },

        // Chat history persistence methods
        getStorageKey() {
            return `chat_history_${this.occasionId}_${this.participantId || 'guest'}`
        },

        saveChatHistory() {
            try {
                const chatData = {
                    responses: this.responses,
                    rateLimitRemaining: this.rateLimitRemaining,
                    typedResponseIds: Array.from(this.typedResponseIds), // Convert Set to Array for storage
                    // EVT-2560: persisted so a page reload does not reopen a conversation a human
                    // has already taken over. Without it, refreshing would hand the participant a
                    // live composer again and let them escalate the same problem twice.
                    sessionEnded: this.sessionEnded,
                    timestamp: Date.now()
                }
                localStorage.setItem(this.getStorageKey(), JSON.stringify(chatData))
            } catch (error) {
                console.warn('Failed to save chat history:', error)
            }
        },

        loadChatHistory() {
            try {
                const stored = localStorage.getItem(this.getStorageKey())
                if (stored) {
                    const chatData = JSON.parse(stored)

                    // Check if the stored data is not too old (configurable hours)
                    const maxAge = this.historyExpiryHours * 60 * 60 * 1000 // Convert hours to milliseconds
                    if (Date.now() - (chatData.timestamp || 0) < maxAge) {
                        this.responses = chatData.responses || []
                        this.rateLimitRemaining = chatData.rateLimitRemaining || 10
                        this.sessionEnded = !!chatData.sessionEnded

                        // Restore typed response IDs and ensure all loaded responses are marked as typed
                        this.typedResponseIds = new Set(chatData.typedResponseIds || [])
                        // Mark ALL loaded responses as already typed to prevent typewriter effect
                        this.responses.forEach(response => {
                            if (response.id && !response.isPending) {
                                this.typedResponseIds.add(response.id)
                            }
                        })

                        // Scroll to bottom after loading history
                        this.$nextTick(() => {
                            this.scrollToBottom()
                        })
                    } else {
                        // Remove old data
                        this.clearChatHistory()
                    }
                }
            } catch (error) {
                console.warn('Failed to load chat history:', error)
            }
        },

        clearChatHistory() {
            try {
                localStorage.removeItem(this.getStorageKey())
            } catch (error) {
                console.warn('Failed to clear chat history:', error)
            }
            // EVT-2566: end the session too, not just the visible transcript. Clearing used to be
            // display-only — the customer saw an empty thread while every following message still
            // grouped into the same admin inbox row as everything before it, because the session
            // identifier was a permanent per-browser value this never touched.
            this.resetChatSession(this.occasionId, this.participantId)
            this.responses = []
            this.rateLimitRemaining = 10
            this.typedResponseIds.clear() // Clear the typed response tracking
            this.structuredInputValue = ''
            this.structuredInputError = ''
            this.sessionEnded = false
        },

        // Logo and branding helper methods
        handleLogoError(event) {
            console.warn('Failed to load logo:', this.logoUrl)
            // Hide the broken image and show fallback
            event.target.style.display = 'none'
            // You could emit an event here to notify parent component
            this.$emit('logo-load-error', this.logoUrl)
        },

        darkenColor(color, percent) {
            // Convert hex to RGB, darken, and convert back
            const hex = color.replace('#', '')
            const r = parseInt(hex.substr(0, 2), 16)
            const g = parseInt(hex.substr(2, 2), 16)
            const b = parseInt(hex.substr(4, 2), 16)
            
            const darkenedR = Math.floor(r * (100 - percent) / 100)
            const darkenedG = Math.floor(g * (100 - percent) / 100)
            const darkenedB = Math.floor(b * (100 - percent) / 100)
            
            return `rgb(${darkenedR}, ${darkenedG}, ${darkenedB})`
        }
    }
}
</script>

<style scoped>
.customer-service-agent {
    display: grid;
    grid-template-rows: 1fr auto;
    height: 100%;
    max-width: 100%;
    border-radius: 8px;
    overflow: hidden;
}


/* Chat Messages Container */
.chat-messages {
    overflow-y: auto;
    scroll-behavior: smooth;
    min-height: 0;
    padding-right:3px;
}

/* Welcome Message */
.welcome-message {
    margin-bottom: 2rem;
    padding: 2rem 1rem;
}

.welcome-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 400px;
    margin: 0 auto;
}

.welcome-logo {
    margin-bottom: 1.5rem;
}

.logo-placeholder {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #007bff, #0056b3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 32px;
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.logo-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.welcome-text {
    color: #495057;
    font-size: 1.1rem;
    line-height: 1.6;
    font-weight: 400;
}

.welcome-text p {
    margin: 0;
}

/* Message Layout */
.bot-message, .user-message {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1rem;
}

.user-message {
    justify-content: flex-end;
}

.user-message .message-content {
    max-width: 75%;
}

.bot-message .message-content.full-width {
    width: 100%;
    max-width: 100%;
}

/* Message Bubbles */
.message-bubble {
    padding: 0.75rem 1rem;
    border-radius: 18px;
    margin-bottom: 0.5rem;
    word-wrap: break-word;
}

.bot-bubble {
    background: #fff;
    border: 1px solid #e9ecef;
    border-bottom-left-radius: 6px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.user-bubble {
    background: #007bff;
    color: white;
    border-bottom-right-radius: 6px;
    margin-left: auto;
    text-align: right;
}

/* Typing Indicator */
.typing-indicator-simple {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 1rem 0;
}

.typing-dots {
    display: flex;
    gap: 4px;
}

.typing-dots .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #6c757d;
    animation: typing-dot 1.4s infinite ease-in-out;
}

.typing-dots .dot:nth-child(1) {
    animation-delay: 0s;
}

.typing-dots .dot:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-dots .dot:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing-dot {
    0%, 80%, 100% {
        transform: scale(0.8);
        opacity: 0.5;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

/* Chat Response Styling Override */
.chat-response {
    margin-bottom: 0;
}

/* Session ended (EVT-2560) — replaces the composer once a handoff is recorded */
.session-ended {
    text-align: center;
    padding: 0.5rem 0;
}

.session-ended__note {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
}

.session-ended__restart {
    padding: 0.5rem 1.25rem;
    border: none;
    border-radius: 8px;
    background: #007bff;
    color: #fff;
    font-size: 0.9rem;
    cursor: pointer;
}

.session-ended__restart:hover {
    background: #0069d9;
}

/* Structured Input Field (EVT-2460) */
.structured-input {
    margin-top: 0.5rem;
    padding: 0.75rem 1rem;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
}

.structured-input__label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.structured-input__row {
    display: flex;
    gap: 0.5rem;
}

.structured-input__field {
    flex: 1;
    padding: 0.5rem 0.75rem;
    border: 1px solid #ced4da;
    border-radius: 8px;
    font-size: 0.9rem;
}

.structured-input__field:disabled {
    background: #f1f3f5;
}

.structured-input__submit {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 8px;
    background: #007bff;
    color: #fff;
    font-size: 0.9rem;
    cursor: pointer;
}

.structured-input__submit:disabled {
    background: #adb5bd;
    cursor: not-allowed;
}

.structured-input__error {
    margin-top: 0.5rem;
    color: #dc3545;
    font-size: 0.8rem;
}

/* Fixed Input Container */
.chat-input-container {
    background: #fff;
    padding-top:1rem;
}

/* Clear History Button */
.clear-history-container {
    display: flex;
    justify-content: center;
    margin-bottom: 0.75rem;
}

.clear-history-container.top-position {
    justify-content: center;
    margin-bottom: 0.5rem;
    padding-right: 0.5rem;
}

.clear-history-btn {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    background: transparent;
    border: none;
    color: #9ca3af;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.clear-history-btn:hover {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
}

.clear-history-btn:active {
    transform: scale(0.98);
}

.clear-history-btn i {
    font-size: 0.7rem;
}

.clear-history-btn span {
    font-weight: 400;
}

/* Conversation Pair Styling */
.conversation-pair {
    margin-bottom: 1.5rem;
}

.conversation-pair:last-child {
    margin-bottom: 0;
}

/* Scrollbar Styling */
.chat-messages::-webkit-scrollbar {
    width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Mobile Responsive */
@media (max-width: 768px) {
    .customer-service-agent {
        border-radius: 0;
        height: 100vh; /* Ensure full viewport height on mobile */
        max-height: 100vh;
    }

    .chat-messages {
        /* Remove max-height restriction to allow full height */
        min-height: 0; /* Allow grid to work properly */
    }

    .message-content {
        max-width: 85%;
    }

    .message-bubble {
        padding: 0.5rem 0.75rem;
        font-size: 0.9rem;
    }

    .welcome-message {
        padding: 1.5rem 0.75rem;
    }

    .logo-placeholder,
    .logo-image {
        width: 60px;
        height: 60px;
    }
    
    .logo-placeholder {
        font-size: 24px;
    }

    .welcome-text {
        font-size: 1rem;
    }

    .clear-history-btn {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
    }

    .clear-history-btn i {
        font-size: 0.7rem;
    }
}

/* Animation for new messages */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.bot-message, .user-message {
    animation: slideIn 0.3s ease-out;
}

</style>



// WEBPACK FOOTER //
// src/Assets/components/AiAgents/CustomerService/CustomerServiceAgent.vue